import { Search, SearchSort } from '@aipb/gulliver/castor/model';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import {
  CATALOG_SEARCH_LOCAL_STORAGE_KEY,
  CustomRecordItem,
  FormSettingsModel,
  ResultListMode,
  SearchFormMode,
  SearchItem,
  SearchSettingsModel,
  SearchTab
} from '@aipb/gulliver/catalog/model';
import { AppConfigId } from '@aipb/gulliver/env/model';
import { APP_NAME } from '@aipb/gulliver/env/util';
import { selectLoading, updateLoading, withLoading } from '@aipb/shared/util/state';
import { inject, Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteAllEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  selectEntity,
  updateEntities,
  withEntities
} from '@ngneat/elf-entities';
import { excludeKeys, localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import isArray from 'lodash-es/isArray';
import { map } from 'rxjs/operators';

function mapSearchSettingsToObject(settings: string[] = [], defaults: SearchSettingsModel = {}) {
  const o: SearchSettingsModel = {};

  if (settings.length === 0) {
    o.asciiSearch = false;
    o.asterLeft = false;
    o.asterRight = false;
    o.stem = false;
    o.stopwords = false;
    o.synonyms = false;
  }

  for (const s of settings) {
    o[s] = defaults[s] || false;
  }

  return o;
}

function mapFormSettings(settings: FormSettingsModel, defaults: FormSettingsModel): FormSettingsModel {
  const s = {};

  for (const key of Object.keys(settings)) {
    if (!settings[key]) continue;

    s[key] = defaults[key] || false;
  }

  return s;
}

export interface SearchStateConfig {
  tabsLocked: boolean;
  activeTabIndex: number;
  tabs: SearchTab[];
  showFormSettings: boolean;
  showSearchSettings: boolean;
  showFilters: boolean;
  searchSettings: SearchSettingsModel;
  formSettings: FormSettingsModel;
  result_list_mode: ResultListMode;
  searchFormMode: SearchFormMode;
}

export interface SearchProps {
  ui: {
    [configId in AppConfigId]: SearchStateConfig;
  };
}

function initProps(catalogEnv: CatalogEnvService) {
  const configIds = catalogEnv.getConfigIds();

  const ui: {
    [configId in AppConfigId]: SearchStateConfig;
  } = {};

  for (const configId of configIds) {
    ui[configId] = {
      tabsLocked: catalogEnv.getEnv(configId).defaultLockResultTabs,
      activeTabIndex: -1,
      tabs: [],
      showFormSettings: false,
      showSearchSettings: false,
      searchSettings: mapSearchSettingsToObject(
        catalogEnv.getEnv(configId).searchSettings,
        catalogEnv.getEnv(configId).defaultSearchSettings
      ),
      formSettings: mapFormSettings(
        catalogEnv.getEnv(configId).formSettings,
        catalogEnv.getEnv(configId).defaultFormSettings
      ),
      result_list_mode: catalogEnv.getEnv(configId).defaultResultListMode,
      showFilters: catalogEnv.getEnv(configId).showFilters,
      searchFormMode: catalogEnv.getEnv(configId).searchFormMode
    };
  }

  return {
    loading: false,
    ui: ui
  };
}

@Injectable({
  providedIn: 'root'
})
export class SearchRepository {
  catalogEnv = inject(CatalogEnvService);
  store = createStore(
    { name: 'catalog-search' },
    withEntities<SearchItem>(),
    withProps<SearchProps>(initProps(this.catalogEnv)),
    withLoading()
  );
  loading$ = this.store.pipe(selectLoading());
  items$ = this.store.pipe(
    selectAllEntities(),
    map((items) => items.sort((a, b) => a.search_at - b.search_at))
  );
  resultListMode$ = this.store.pipe(select((state) => state.ui[this.configId].result_list_mode));
  showFilters$ = this.store.pipe(select((state) => state.ui[this.configId].showFilters));
  tabsLocked$ = this.store.pipe(select((state) => state.ui[this.configId].tabsLocked));
  activeTabIndex$ = this.store.pipe(select((state) => state.ui[this.configId].activeTabIndex));
  tabs$ = this.store.pipe(select((state) => state.ui[this.configId].tabs));
  searchSettings$ = this.store.pipe(select((state) => state.ui[this.configId].searchSettings));
  formSettings$ = this.store.pipe(select((state) => state.ui[this.configId].formSettings));
  showFormSettings$ = this.store.pipe(select((state) => state.ui[this.configId].showFormSettings));
  showSearchSettings$ = this.store.pipe(select((state) => state.ui[this.configId].showSearchSettings));
  searchFormMode$ = this.store.pipe(select((state) => state.ui[this.configId].searchFormMode));
  distanceSearch$ = this.store.pipe(select((state) => state.ui[this.configId].formSettings.distanceSearch));
  private appName = inject(APP_NAME);
  persist = persistState(this.store, {
    key: `${this.appName}-${CATALOG_SEARCH_LOCAL_STORAGE_KEY}`,
    storage: localStorageStrategy,
    source: () => this.store.pipe(excludeKeys(['ids', 'entities', 'loading']))
  });

  get configId() {
    return this.catalogEnv.activeConfigId;
  }

  addTab(tab: SearchTab) {
    this.store.update((state) => {
      const tabs = state.ui[this.configId].tabs;

      if (state.ui[this.configId].tabsLocked) {
        tabs.pop();
      }

      tabs.push(tab);

      return {
        ...state,
        ui: {
          ...state.ui,
          [this.configId]: {
            ...state.ui[this.configId],
            tabs
          }
        }
      };
    });
  }

  addSearch(search: SearchItem) {
    this.store.update(addEntities(search));
  }

  updateSearch(id: string, params: Partial<SearchItem>) {
    this.store.update(updateEntities(id, params));
  }

  updateSearchUI(id: string, item: SearchItem, search: Search<CustomRecordItem>, sort: SearchSort[]) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          tabs: state.ui[this.configId].tabs.map((t) => {
            if (t.id === id) {
              return <SearchTab>{
                ...t,
                title: item.prettyQuery,
                resultCount: search.found,
                solrSearchParams: {
                  ...t.solrSearchParams,
                  query: isArray(t.solrSearchParams.query) ? t.solrSearchParams.query : search.q,
                  sort: sort,
                  start: search.pageSize * search.pageNum,
                  rows: search.pageSize
                },
                settings: isArray(t.solrSearchParams.query) ? t.settings : {}
              };
            }

            return t;
          })
        }
      }
    }));
  }

  getLockResultTabs(): boolean {
    return this.store.getValue().ui[this.configId].tabsLocked;
  }

  selectSearch(id: string) {
    return this.store.pipe(selectEntity(id));
  }

  getShowFilters(): boolean {
    return this.store.getValue().ui[this.configId].showFilters;
  }

  getDistanceSearch(): boolean {
    return this.store.getValue().ui[this.configId].formSettings.distanceSearch;
  }

  isTabsLocked(): boolean {
    return this.store.getValue().ui[this.configId].tabsLocked;
  }

  getSearchSetttings(): SearchSettingsModel {
    return this.store.getValue().ui[this.configId].searchSettings;
  }

  getShowSearchSettings(): boolean {
    return this.store.getValue().ui[this.configId].showSearchSettings;
  }

  getActiveTabIndex(): number {
    return this.store.getValue().ui[this.configId].activeTabIndex;
  }

  getActiveTab(): SearchTab {
    const i = this.getActiveTabIndex();

    if (i >= 0 && i < this.getTabCount()) {
      return this.getTab(i);
    }

    return null;
  }

  hasTab(id: string): boolean {
    return this.getTabs().some((t) => t.id === id);
  }

  getSearch(id: string): SearchItem {
    return this.store.query(getEntity(id));
  }

  getSearchSettings(): SearchSettingsModel {
    return this.store.getValue().ui[this.configId].searchSettings;
  }

  getTabs(): SearchTab[] {
    return this.store.getValue().ui[this.configId].tabs;
  }

  getTabById(id: string): SearchTab {
    return this.getTabs().find((t) => t.id === id);
  }

  getTab(i: number) {
    return this.getTabs()[i];
  }

  getTabCount(): number {
    return this.getTabs().length;
  }

  removeSearches() {
    this.store.update(deleteAllEntities());
  }

  removeTabs() {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          tabs: []
        }
      }
    }));
  }

  removeSearch(id: string): void {
    this.store.update(deleteEntities(id));
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          tabs: state.ui[this.configId].tabs.filter((t) => t.id !== id)
        }
      }
    }));
  }

  setShowFilters(showFilters: boolean) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          showFilters
        }
      }
    }));
  }

  public setActiveTabIndex(activeTabIndex: number) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          activeTabIndex
        }
      }
    }));
  }

  public toggleSearchSetting(key: string): void {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          searchSettings: {
            ...state.ui[this.configId].searchSettings,
            [key]: !state.ui[this.configId].searchSettings[key]
          }
        }
      }
    }));
  }

  public toggleFormSetting(key: string): void {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          formSettings: {
            ...state.ui[this.configId].formSettings,
            [key]: !state.ui[this.configId].formSettings[key]
          }
        }
      }
    }));
  }

  public toggleShowFormSettings(): void {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          showFormSettings: !state.ui[this.configId].showFormSettings
        }
      }
    }));
  }

  public toggleShowSearchSettings(): void {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          showSearchSettings: !state.ui[this.configId].showSearchSettings
        }
      }
    }));
  }

  toggleShowFilters() {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          showFilters: !state.ui[this.configId].showFilters
        }
      }
    }));
  }

  public setTabsLocked(tabsLocked = true): void {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          tabsLocked
        }
      }
    }));
  }

  public setResultListMode(result_list_mode: ResultListMode): void {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          result_list_mode
        }
      }
    }));
  }

  public setSearchFormMode(searchFormMode: SearchFormMode): void {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          searchFormMode
        }
      }
    }));
  }

  setLoading(loading: boolean) {
    this.store.update(updateLoading(loading));
  }

  reset() {
    this.store.reset();
  }
}
